// File: pages/admin/AdminUsers.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Loader2, AlertCircle, Eye, UserPlus, Search, X } from 'lucide-react';
import { authAPI } from '../../services/api';
import './css/AdminUsers.css';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await authAPI.getUsers();
        setUsers(response.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Impossible de charger les utilisateurs');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(user => 
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const closeAlert = () => setError('');

  if (loading) {
    return (
      <div className="admin-users-loading-container">
        <div className="admin-users-loading-content">
          <Loader2 className="admin-users-loading-spinner" />
          <p className="admin-users-loading-text">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-users-container">
      {/* Header */}
      <header className="admin-users-header">
        <div className="admin-users-header-content">
          <div className="admin-users-header-left">
            <div className="admin-users-header-icon-wrapper">
              <Users className="admin-users-header-icon" />
            </div>
            <div className="admin-users-header-text">
              <h1 className="admin-users-title">Gestion des utilisateurs</h1>
              <p className="admin-users-subtitle">Voir et gerer les comptes clients</p>
            </div>
          </div>
          <Link to="/admin/users/new" className="admin-users-add-btn">
            <UserPlus className="admin-users-add-icon" />
            <span>Ajouter un utilisateur</span>
          </Link>
        </div>
      </header>

      {/* Alerts */}
      {error && (
        <div className="admin-users-alerts">
          <div className="admin-users-alert admin-users-alert-error">
            <AlertCircle className="admin-users-alert-icon" />
            <p className="admin-users-alert-text">{error}</p>
            <button className="admin-users-alert-close" onClick={closeAlert}>
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="admin-users-stats-grid">
        <div className="admin-users-stat-card">
          <div className="admin-users-stat-icon admin-users-stat-total">
            <Users size={24} />
          </div>
          <div className="admin-users-stat-content">
            <span className="admin-users-stat-label">Total utilisateurs</span>
            <span className="admin-users-stat-value">{users.length}</span>
          </div>
        </div>
        <div className="admin-users-stat-card">
          <div className="admin-users-stat-icon admin-users-stat-admin">
            <Users size={24} />
          </div>
          <div className="admin-users-stat-content">
            <span className="admin-users-stat-label">Administrateurs</span>
            <span className="admin-users-stat-value">
              {users.filter(u => u.isAdmin).length}
            </span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-users-filters-bar">
        <div className="admin-users-search-wrapper">
          <Search className="admin-users-search-icon" />
          <input
            type="text"
            placeholder="Rechercher par email ou nom..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-users-search-input"
          />
        </div>
      </div>

      {/* Main Content */}
      <main className="admin-users-card">
        <div className="admin-users-card-content">
          <div className="admin-users-card-header">
            <h2 className="admin-users-card-title">
              <Users size={20} />
              <span>Liste des utilisateurs</span>
              <span className="admin-users-count-badge">{filteredUsers.length}</span>
            </h2>
          </div>
          
          <div className="admin-users-table-wrapper">
            {filteredUsers.length === 0 ? (
              <div className="admin-users-empty">
                <Users className="admin-users-empty-icon" />
                <h3 className="admin-users-empty-title">Aucun utilisateur trouve</h3>
                <p className="admin-users-empty-text">
                  {searchTerm 
                    ? 'Aucun utilisateur ne correspond a votre recherche' 
                    : 'Aucun utilisateur n\'est enregistre dans le systeme'}
                </p>
              </div>
            ) : (
              <table className="admin-users-table">
                <thead>
                  <tr className="admin-users-thead-tr">
                    <th className="admin-users-th">ID</th>
                    <th className="admin-users-th">Email</th>
                    <th className="admin-users-th">Nom</th>
                    <th className="admin-users-th">Telephone</th>
                    <th className="admin-users-th">Role</th>
                    <th className="admin-users-th">Cree</th>
                    <th className="admin-users-th">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="admin-users-tr">
                      <td className="admin-users-td admin-users-td-id">
                        #{String(user.id).slice(0, 8)}
                      </td>
                      <td className="admin-users-td admin-users-td-email">{user.email}</td>
                      <td className="admin-users-td admin-users-td-name">
                        {user.firstName} {user.lastName}
                      </td>
                      <td className="admin-users-td admin-users-td-phone">
                        {user.phoneNumber || 'N/D'}
                      </td>
                      <td className="admin-users-td">
                        <span className={`admin-users-badge ${user.isAdmin ? 'admin-users-badge-admin' : 'admin-users-badge-user'}`}>
                          {user.isAdmin ? 'Admin' : 'Utilisateur'}
                        </span>
                      </td>
                      <td className="admin-users-td admin-users-td-date">
                        {new Date(user.createdAt).toLocaleDateString('fr-FR', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </td>
                      <td className="admin-users-td admin-users-td-actions">
                        <Link to={`/admin/users/${user.id}`} className="admin-users-action-btn">
                          <Eye size={16} />
                          <span>Voir</span>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminUsers;
